<template>
  <div>
    <div class="fixed-heading">
      <div class="card__general side-panel__header">
        <div class="card__title side-panel__announcement-card__title side-panel__announcement-header-card__title">
          {{ challenge.id ? $t('edit_challenge_panel_title', { challenge: challengeLabel }) : $t('add_challenge_panel_title_v2', { challenge: challengeLabel }) }}
        </div>
        <DsButton icon="trash" variant="minimal" @click="remove" size="small"
                  class="side-panel__announcement-header-icon" style="margin-left: auto"/>
        <DsButton icon="remove" variant="minimal" @click="close" size="small"
                  class="side-panel__announcement-header-icon"/>
      </div>
    </div>
    <div class="scrollable side-panel__scrollable__content has-padding">
      <div class="side-panel__announcement-form-container">
        <form>
          <form-group :label="$t('add_announcement_title')" :errors="errors.title" :required="true">
            <ds-input v-model="challenge.title" is-simplified/>
          </form-group>

          <FormGroup :label="$t('challenges_manage_panel_for_which_company')">
            <ActorSearch
              v-if="isMember || isOwner"
              @set-actor="handleActorChange($event, 'add')"
              @remove-actor="handleActorChange($event, 'remove')"
              :return-sql-id="true"
              :model-value="challenge.actor"
            />
            <div v-else>
              <!-- If the user is not a member or owner, then show his actor claims -->
              <Dropdown
                :options="actorOptions"
                v-model="challenge.actor"
                @update:model-value="handleActorChange($event, 'add')"
                is-simplified
              />
            </div>
          </FormGroup>

          <FormGroup :label="$t('challenges_manage_panel_who_to_contact')" v-if="challenge.actor">
            <Dropdown
              v-if="!isLoadingUserSuggestions"
              :placeholder="'Select or search a contributor'"
              v-model="challenge.contact"
              is-simplified
              :options="displayedUserSuggestions"
              search
              allow-clear
            />
            <Checkbox is-simplified :model-value="challenge.isAnonymous" v-model="challenge.isAnonymous" @update:model-value="handleIsAnonymousChange"
                      label="Anonymize the organisation and contact person"/>
          </FormGroup>


          <div class="side-panel__challenge_subtitle">
            {{ $t('manage_challenge_subtitle_description') }}
          </div>
          <br>
          <form-group :label="$t('manage_challenge_description', {challenge: challengeLabel})"
                      :errors="errors.description" :required="true">
            <rich-text-area v-model="challenge.description" :minimal="true"/>
            <GenerativeTextArea
              style="margin-top: 0.5rem;"
              :feedback-type="GenerativeUserFeedbackType.challengeDescription"
              :enable-feedback="false"
              :loading="generatingChallengeDescription"
              :model-value="challenge.description"
              :enable-text-area="false"
              @generate="generateChallengeDescription"
              @reGenerate="regenerateChallengeDescription"
              :disabled="!challenge.title.length"
            />
          </form-group>
          <form-group :label="$t('challenge_background_of_the_challenge', { challenge: challengeLabel})" :errors="errors.background" :required="true">
            <rich-text-area v-model="challenge.background" :minimal="true"/>
            <GenerativeTextArea
              style="margin-top: 0.5rem;"
              :feedback-type="GenerativeUserFeedbackType.challengeBackground"
              :enable-feedback="false"
              :loading="generatingChallengeBackground"
              :model-value="challenge.background"
              :enable-text-area="false"
              @generate="generateChallengeBackground"
              @reGenerate="regenerateChallengeBackground"
              :disabled="!challenge.title.length || challenge.description.length < 8"
            />
          </form-group>
          <form-group :label="$t('manage_challenge_goal')" :errors="errors.objective" :required="true">
            <rich-text-area v-model="challenge.objective" :minimal="true"/>
            <GenerativeTextArea
              style="margin-top: 0.5rem;"
              :feedback-type="GenerativeUserFeedbackType.challengeObjective"
              :enable-feedback="false"
              :loading="generatingChallengeObjective"
              :model-value="challenge.objective"
              :enable-text-area="false"
              @generate="generateChallengeObjective"
              @reGenerate="regenerateChallengeObjective"
              :disabled="!challenge.title.length || challenge.description.length < 8"
            />
          </form-group>
          <!--          <form-group label="Goal of the challenge" :errors="errors.goals" :required="true">
                      &lt;!&ndash;              @update:modelValue="handleChallengeGoalChanged"&ndash;&gt;
                      <dropdown
                        :options="challengeGoalOptions"
                        :disabled="!isMember"
                        v-model="challenge.goals"
                        multiple
                        is-simplified
                      />
                    </form-group>-->
          <TaxonomyQuickEditChallenges
            :model="challenge"
            :show-active-taxonomies="true"
            @add-taxonomy="addTaxonomy"
            @remove-taxonomy="removeTaxonomy"
          />
          <form-group :label="$t('add_challenge_invite_only')" v-if="isOwner" v-model="challenge.visibility"
                      :errors="errors.visibility" class="side-panel__challenge_invite_only">
            <DsButton v-for="(visibilityOption, index) in visibilityOptions" :key="'visibility' + index"
                      :label="visibilityOption.label" @click="toggleVisibilityOption(visibilityOption)"
                      :variant="challenge.visibility == visibilityOption.value ? 'rounded-active' : 'rounded-secondary'"/>
          </form-group>
          <form-group label="Link to community"
                      :errors="errors.invite_only" class="side-panel__challenge_invite_only">
            <dropdown
              :options="communityOptions"
              v-model="challenge.communityIds"
              :disabled="!isMember"
              allow-clear
              is-simplified
              multiple
            />

          </form-group>
          <br>
          <div class="side-panel__challenge_subtitle">
            {{ $t('manage_challenge_subtitle_requirements') }}
          </div>
          <br>
          <div style="display: flex">
            <div style="width: 50%">
              <form-group :label="$t('add_challenge_sdg')" :errors="errors.sdgs">
                <ul class="side-panel__challenge_sdgs">
                  <li :title="item" v-for="(item, index) in sdgOptions"
                      class="sdg-filter"
                      v-tooltip.top="translatedSustainabilityGoals[index]"
                      :class="{ 'modal__body__active-filter-img': isSdgEnabledForChallenge(item)}">
                    <img :src="sdgImagesPath + (index + 1) + '.jpg'" :alt="translatedSustainabilityGoals[index]"
                         @click="handleToggleSustainabilityGoal(item)">
                  </li>
                  <li></li>
                  <li></li>
                  <li></li>
                  <li></li>
                </ul>
              </form-group>
              <form-group :label="$t('add_challenge_status')" v-model="challenge.status" v-if="isOwner"
                          :errors="errors.status">
                <dropdown v-model="challenge.status" :options="statusOptions" autoresize is-simplified/>
              </form-group>
              <!--              <form-group label="Growth stage" v-if="growthStageOptions">-->
              <!--                <checkbox v-for="(stage, index) in growthStageOptions" :key="'stage' + index" :label="stage.name" v-model="challenge.growth_stages" inline multiple :val="stage.id"/>-->
              <!--              </form-group>-->
            </div>
            <div style="width: 50%; padding-left: 20px">
              <form-group :label="$t('add_challenge_geography_scope')" :info-text="$t('challenge_geoscope_info')">
                <dropdown v-model="challenge.geographicalScope" :options="geographyOptions" search :multiple="true" is-simplified/>
              </form-group>
              <form-group :label="$t('add_challenge_end_date')"
                          :info-text="$t('challenge_end_date_info', {challenge: challengeLabel})">
                <datepicker :model-value="challenge.endDate" @update:modelValue="challenge.endDate = $event"
                            :allow-reset="true" is-simplified/>
              </form-group>
              <form-group :label="$t('add_announcement_tags')" :errors="errors.tags"
                          :info-text="$t('challenge_tags_info', {challenges: challengesLabel})">
                <AutocompleteTagInput :placeholder="$t('add_announcement_tags_placeholder')" @tagsChanged="updateTags" is-simplified
                                      :tags="challenge.tags" @tagChanged="handleTagChanged" :options="tagOptions"/>
              </form-group>
              <form-group :label="$t('add_announcement_image')" :errors="errors.image"
                          :info-text="$t('add_challenge_image_help_text', {challenge: challengeLabel})">
                <image-input v-model="challenge.originalImage" @update:modelValue="updateImage" width="200"
                             height="100"/>
              </form-group>
            </div>
          </div>
        </form>
      </div>
    </div>
    <action-bar editing="1" class="side-panel__announcement-bottom-section">
      <DsButton variant="rounded" :loading="saving" :label="saveButtonLabel" @click="save()" :disabled="!canSave || saving"/>
    </action-bar>
  </div>
</template>

<script lang="ts">
  import { Challenges } from '../../Domain/Challenge/Api/challenges'
  import { fetchSdgs } from '../../api/taxonomies'
  import { trackHeapEvent } from '../../util/analytics'
  import { fetchActors, searchContacts } from '../../api/actors'
  import TagsMixin from '../../util/TagsMixin'
  import TranslationsMixin from '../../util/TranslationsMixin'
  import UserMixin from '../../util/UserMixin'
  import { MUTATION_TYPES as UI_MUTATION_TYPES } from '../../store/modules/ui'
  import { ACTION_TYPES as CHALLENGES_ACTION_TYPES, MUTATION_TYPES } from '../../store/modules/challenges'
  import { GenerativeType, GenerativeUserFeedbackType } from '../../Domain/Challenge/enums'
  import { generateText } from '../../api/generative'
  import { sustainabilityGoals, sustainabilityGoalsNL } from '../../constants/config'
  import { CONTINENTS, COUNTRIES } from '../../constants/countries'
  import MODAL_IDS from '../../constants/modal-ids'
  import { Challenge } from '../../Domain/Challenge/Types/challenge-types'

  import DsTextarea from '../Form/DsTextarea.vue'
  import AutocompleteTagInput from '../../components/Form/AutocompleteTagInput.vue'
  import ActionBar from '../../components/Form/ActionBar.vue'
  import TaxonomyQuickEditChallenges from '../../Domain/Challenge/Components/TaxonomyQuickEditChallenges.vue'
  import GenerativeTextArea from '../../components/TextArea/GenerativeTextArea.vue'
  import FormGroup from '../../components/Form/FormGroup.vue'
  import AutocompleteDropdown from '../../components/Dropdown/AutocompleteDropdown.vue'
  import ActorSearch from '../../components/ActorSearch/ActorSearch.vue'
  import Checkbox from '../../components/Form/Checkbox.vue'
  import ImageInput from '../../components/Form/ImageInput.vue'
  import Dropdown from '../../components/Dropdown/Dropdown.vue'
  import RichTextArea from '../../components/TextArea/RichTextArea.vue'
  import Datepicker from '../../components/Form/Datepicker.vue'
  import ToggleButton from '../../components/Form/ToggleButton.vue'

  interface Actors {
    searchQuery: string
    lastUsedSearchQuery: string
    searching: boolean
    results: Array<{
      id: number
      name: string
    }>
  }

  interface VisibilityOption {
    label: string
    value: string
  }

  interface ComponentData {
    challenge: Challenge
    actors: Actors
    isLoadingUserSuggestions: boolean
    generatingChallengeDescription: boolean
    generatingChallengeBackground: boolean
    generatingChallengeObjective: boolean
    displayedUserSuggestions: Array<{ label: string; value: number }>
    visibilityOptions: VisibilityOption[]
    saving: boolean
    errors: Record<string, any>
    statusOptions: Array<{ label: string; value: string }>
    challengeGoalOptions: Array<{ label: string; value: string }>
    sdgOptions: Array<{ label: string; value: number }>
  }

  export default {
    name: 'ManageChallengePanel',
    mixins: [TagsMixin, TranslationsMixin, UserMixin],
    components: {
      TaxonomyQuickEditChallenges,
      GenerativeTextArea,
      FormGroup,
      AutocompleteDropdown,
      ActorSearch,
      Checkbox,
      ActionBar,
      DsTextarea,
      ImageInput,
      AutocompleteTagInput,
      Dropdown,
      RichTextArea,
      Datepicker,
      ToggleButton,
    },
    props: {
      challengeData: {
        type: Object as () => Challenge | null,
        default: null
      }
    },
    data(): ComponentData {
      return {
        challenge: {
          title: '',
          actor: null,
          description: '',
          background: '',
          status: 'new',
          image: null,
          tags: [],
          objective: '',
          endDate: '',
          visibility: '',
          geographicalScope: [],
          sdgs: [],
          communityIds: [],
          goals: [],
          isAnonymous: false,
          activities: [],
          industries: [],
          technology: [],
          domains: [],
          stages: [],
          contact: 0
        } as Challenge,
        actors: {
          searchQuery: '',
          lastUsedSearchQuery: '',
          searching: false,
          results: [],
        } as Actors,
        isLoadingUserSuggestions: false,
        generatingChallengeDescription: false,
        generatingChallengeBackground: false,
        generatingChallengeObjective: false,
        displayedUserSuggestions: [],
        visibilityOptions: [{ label: 'Private', value: 'private' }, { label: 'Public', value: 'public' }],
        saving: false,
        errors: {},
        statusOptions: [
          { label: 'New', value: 'new' },
          { label: 'Open', value: 'open' },
          { label: 'Solved', value: 'solved' },
        ],
        challengeGoalOptions: [
          { label: 'Looking for partners', value: 'partners', },
          { label: 'Knowledge', value: 'knowledge', }
        ],
        sdgOptions: [],
      }
    },
    computed: {
      GenerativeUserFeedbackType() {
        return GenerativeUserFeedbackType
      },
      isEditing(): boolean {
        return !!this.challenge.id
      },
      canSave() {
        const hasAtLeastOneTaxonomy =
          this.challenge.activities.length > 0 ||
          this.challenge.industries.length > 0 ||
          this.challenge.technology.length > 0 ||
          this.challenge.domains.length > 0 ||
          this.challenge.stages.length > 0

        return this.challenge.title &&
          this.challenge.description &&
          this.challenge.objective &&
          hasAtLeastOneTaxonomy
      },
      communityId() {
        return this.$route.params.communityId
      },
      communityOptions() {
        return this.$store.state.communities.listData.data.map((community) => {
          return {
            label: community.name,
            value: community.id
          }
        })
      },
      translatedSustainabilityGoals() {
        return this.$store.getters.activeLocale === 'nl' ? sustainabilityGoalsNL : sustainabilityGoals
      },
      sdgImagesPath() {
        return this.$store.getters.activeLocale === 'nl' ? '/images/SDG_nl/' : '/images/SDG/'
      },
      geographyOptions() {
        var geographyOptions = { ...CONTINENTS, ...COUNTRIES }

        var options = []

        Object.keys(geographyOptions)
          .forEach(key => {
            options.push({
              label: geographyOptions[key],
              value: key
            })
          })

        return options
      },
      saveButtonLabel() {
        if (this.isEditMode) {
          if (this.challenge.curation === 'in_progress' && (this.isMember || (this.isPortfolioMember && this.$route.name === 'datalab'))) {
            return 'Approve & Update'
          } else {
            return this.$t('edit_challenge_button', { challenge: this.challengeLabel })
          }
        }
        return this.$t('add_challenge_save')
      },
      isEditMode() {
        return !!this.challenge.id
      },
      actorOptions(): Array<{ value: number; label: string }> {
        return this.claimedActors.map(actor => ({
          value: actor.id,
          label: actor.name
        }))
      }
    },
    methods: {
      addTaxonomy(taxonomyOption) {
        this.$store.commit(MUTATION_TYPES.ADD_TAXONOMY_TO_CHALLENGE, {
          challenge: this.challenge,
          taxonomyValue: taxonomyOption
        })
      },
      removeTaxonomy(taxonomyOption) {
        this.$store.commit(MUTATION_TYPES.REMOVE_TAXONOMY_FROM_CHALLENGE, {
          challenge: this.challenge,
          taxonomyOption: taxonomyOption
        })
      },
      generateChallengeDescription(context) {
        this.generatingChallengeDescription = true
        generateText(GenerativeType.challengeDescription, this.challenge, context)
          .then((response) => {
            this.challenge.description = response.result
          })
          .finally(() => {
            this.generatingChallengeDescription = false
          })
      },
      regenerateChallengeDescription(context) {
        this.generatingChallengeDescription = true
        this.challenge.previousText = this.challenge.description
        generateText(GenerativeType.challengeDescription, this.challenge, context)
          .then((response) => {
            this.challenge.description = response.result
          })
          .finally(() => {
            this.generatingChallengeDescription = false
          })
      },
      generateChallengeObjective(context) {
        this.generatingChallengeObjective = true
        generateText(GenerativeType.challengeObjective, this.challenge, context)
          .then((response) => {
            this.challenge.objective = response.result
          })
          .finally(() => {
            this.generatingChallengeObjective = false
          })
      },
      regenerateChallengeObjective(context) {
        this.generatingChallengeObjective = true
        this.challenge.previousText = this.challenge.objective
        generateText(GenerativeType.challengeObjective, this.challenge, context)
          .then((response) => {
            this.challenge.objective = response.result
          })
          .finally(() => {
            this.generatingChallengeObjective = false
          })
      },
      generateChallengeBackground(context) {
        this.generatingChallengeBackground = true
        generateText(GenerativeType.challengeBackground, this.challenge, context)
          .then((response) => {
            this.challenge.background = response.result
          })
          .finally(() => {
            this.generatingChallengeBackground = false
          })
      },
      regenerateChallengeBackground(context) {
        this.generatingChallengeBackground = true
        this.challenge.previousText = this.challenge.background
        generateText(GenerativeType.challengeBackground, this.challenge, context)
          .then((response) => {
            this.challenge.background = response.result
          })
          .finally(() => {
            this.generatingChallengeBackground = false
          })
      },
      handleIsAnonymousChange(value) {
        this.challenge.isAnonymous = value
      },
      autocompleteSearchTerm(query) {
        if (!this.challenge.actor) return

        let actorId = this.challenge.actor
        if (this.challenge.actor instanceof Object) {
          actorId = this.challenge.actor.id
        }

        return searchContacts(actorId, {
          query: query
        })
          .then(suggestions => {
            const options = []
            //suggestions = this.removeDuplicates(suggestions)

            suggestions
              .forEach(function (user) {
                options.push({
                  label: user.name + ' (' + user.email + ')',
                  value: user.id
                })
              })

            this.displayedUserSuggestions = options

            return options
          })
          .catch(err => {
            console.log(err)
          })
      },
      handleActorChange(event: number | string | null, action: 'add' | 'remove') {
        if (action === 'add') {
          this.challenge.actor = event
          this.autocompleteSearchTerm('')
          this.challenge.contact = null
        } else {
          this.challenge.actor = null
          this.challenge.contact = null
        }
      },
      toggleVisibilityOption(option) {
        this.challenge.visibility = option.value
      },
      handleToggleSustainabilityGoal(sdgToToggle) {
        if (!this.isSdgEnabledForChallenge(sdgToToggle)) {
          this.challenge.sdgs = [...this.challenge.sdgs, sdgToToggle]
        } else {
          this.challenge.sdgs = this.challenge.sdgs.filter(sdg => sdgToToggle.value !== sdg.value)
        }
      },
      isSdgEnabledForChallenge(sdgToFind) {
        if (!this.challenge || !this.challenge.sdgs || !this.challenge.sdgs.length) {
          return false
        }
        return !!this.challenge.sdgs.find((s) => s.value === sdgToFind.value)
      },
      updateImage(image) {
        this.challenge.image = image
      },
      validateForm() {
        this.errors = []

        if (!this.challenge.description || this.challenge.description === '<p></p>') {
          this.errors.description = [this.$t('challenge_description_required')]
        }

        // if (!this.challenge.objective || this.challenge.objective === '<p></p>') {
        //   this.errors.objective = ['Objective is required']
        // }
        //
        if (!this.challenge.title) {
          this.errors.title = [this.$t('challenge_title_required')]
        }

        if (Object.keys(this.errors).length === 0) {
          return true
        }
      },
      async save() {
        if (!this.validateForm() || this.saving) {
          return
        }

        this.saving = true

        try {
          await Challenges.post(this.challenge)
          this.saving = false

          this.$bus.emit(this.isEditMode ? 'challengeUpdated' : 'challengeCreated')
          this.$store.dispatch(CHALLENGES_ACTION_TYPES.FETCH_CHALLENGE_DETAIL, Number(this.challenge.id))
          this.$store.commit(UI_MUTATION_TYPES.HIDE_SIDE_PANEL)
        } catch (errors) {
          this.saving = false
          console.log(errors)
          this.errors = errors
        }

        this.saving = false
      },
      async deleteChallenge() {
        await Challenges.delete(this.challenge.id)

        this.$bus.emit('challengeDeleted')
        trackHeapEvent('manageChallenge.remove')
        this.close()

        if (this.$router.params.communityId) {
          this.$router.push('/communities/' + this.$router.params.communityId + '/challenges')
          return
        }

        this.$router.push('/challenges')
      },
      remove() {
        this.$store.commit(UI_MUTATION_TYPES.SET_MODAL_CONTEXT, {
          name: this.challenge.title,
          resource: this.challenge,
          modalContextType: 'challenge',
        })
        this.$store.commit(UI_MUTATION_TYPES.SHOW_MODAL, MODAL_IDS.DELETE_CONFIRMATION)
      },
      updateTags(tags) {
        this.challenge.tags = tags
      },
      close() {
        this.$store.commit(UI_MUTATION_TYPES.HIDE_SIDE_PANEL)
      },
      handleTagChanged(tag) {
        // Update the options according to the given tag
        this.updateTagOptions(tag)
      },
      getSdgs() {
        //TODO @Jan: can we not replce this by
        // import { sustainabilityGoals } from '../../../constants/config.js'
        fetchSdgs()
          .then(response => {
            this.sdgOptions = response.map(function (sdg) {
              return { label: sdg.name, value: sdg.id }
            })
          })
      },
      async fetchActors() {
        try {
          const response = await fetchActors([])
          this.actors.results = response.data || []
        } catch (error) {
          console.error('Failed to fetch actors:', error)
        }
      }
    },
    async mounted() {
      if (this.challengeData) {
        this.challenge = {
          title: '',
          actor: null,
          description: '',
          background: '',
          status: 'new',
          image: null,
          tags: [],
          objective: '',
          endDate: '',
          visibility: '',
          geographicalScope: [],
          sdgs: [],
          communityIds: [],
          goals: [],
          isAnonymous: false,
          activities: [],
          industries: [],
          technology: [],
          domains: [],
          stages: [],
          ...this.challengeData,
          originalImage: `/api/challenges/${this.challengeData.id}/image`,
        }

        if (this.challengeData.contact) {
          this.challenge.contact = this.challengeData.contact.id
        }
      }

      if (this.communityId && this.challenge.communityIds && this.challenge.communityIds.length === 0) {
        this.challenge.communityIds.push(Number(this.communityId))
      }

      this.getSdgs()

      if (this.challenge.actor) {
        this.isLoadingUserSuggestions = true
        await this.autocompleteSearchTerm('')
        this.isLoadingUserSuggestions = false
      }

      await this.fetchActors()
      await this.autocompleteSearchTerm('')
    },
    created() {
      this.$bus.on('challengeDeleteConfirmation', (context) => {
        this.deleteChallenge(context.resource)
      })
    },
    beforeUnmount() {
      this.$bus.off('challengeDeleteConfirmation')
    },
  }
</script>

<style lang="scss" scoped>
  @import "../../../scss/_variables";

  .side-panel__announcement-form-container {
    :deep(.svg-icon--information-outline) {
      path {
        fill: #555;
      }
    }
  }

  .side-panel__challenge_subtitle {
    text-transform: uppercase;
    color: $color-text-grey-light;
    font-size: 16px;
  }

  .side-panel__challenge_invite_only {
    .button--outline {
      color: var(--primary-community);

      &:hover {
        color: white;
      }
    }
  }

  .side-panel__challenge_sdgs {
    width: 100%;
    display: flex;
    list-style-type: none;
    flex-wrap: wrap;
    flex-direction: row;
    justify-content: space-between;

    li {
      height: 0;
      width: 60px;
    }

    li.sdg-filter {
      cursor: pointer;
      height: 60px;
      margin-bottom: 1px;
      border: 4px solid transparent;

      &:hover {
        border: 4px solid $color-borders;
      }

      &.modal__body__active-filter-img {
        border: 4px solid var(--primary-community-light);
      }
    }

    img {
      width: 100%;
    }
  }
</style>
